import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

export interface TabItem {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  count?: number;
  content: React.ReactNode;
}

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface EntityDetailLayoutProps {
  breadcrumbs?: BreadcrumbItem[];
  header: React.ReactNode;
  sidebar: React.ReactNode;
  tabs: TabItem[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

export function EntityDetailLayout({
  breadcrumbs,
  header,
  sidebar,
  tabs,
  activeTab,
  onTabChange,
}: EntityDetailLayoutProps) {
  const currentTab = tabs.find((t) => t.id === activeTab) || tabs[0];

  return (
    <div className="min-h-full flex flex-col space-y-6 pb-12">
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="flex items-center space-x-1.5 text-xs text-muted-foreground">
          {breadcrumbs.map((crumb, idx) => {
            const isLast = idx === breadcrumbs.length - 1;
            return (
              <React.Fragment key={idx}>
                {idx > 0 && <ChevronRight className="size-3.5 text-muted-foreground/60 shrink-0" />}
                {crumb.href && !isLast ? (
                  <Link
                    to={crumb.href}
                    className="hover:text-foreground transition-colors font-medium hover:underline underline-offset-2"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className={isLast ? "font-semibold text-foreground truncate max-w-[240px] sm:max-w-md" : ""}>
                    {crumb.label}
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      )}

      {/* Top Header Card */}
      {header}

      {/* Two Column Layout: Left Sidebar + Right Tabs & Content */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3.5 space-y-6">
          {sidebar}
        </aside>

        {/* Right Main Content */}
        <main className="lg:col-span-8 xl:col-span-8.5 space-y-4">
          {/* Tab Navigation Pill Bar */}
          <div className="bg-card border border-border/80 rounded-2xl p-1.5 shadow-2xs overflow-x-auto no-scrollbar">
            <div className="flex items-center gap-1 min-w-max">
              {tabs.map((tab) => {
                const isActive = tab.id === (currentTab?.id || activeTab);
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => onTabChange(tab.id)}
                    className={`flex items-center gap-2 px-3.5 py-2 text-xs font-medium rounded-xl transition-all select-none ${
                      isActive
                        ? "bg-primary text-primary-foreground shadow-xs font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/70"
                    }`}
                  >
                    {Icon && <Icon className={`size-3.5 ${isActive ? "text-primary-foreground" : "text-muted-foreground"}`} />}
                    <span>{tab.label}</span>
                    {typeof tab.count === "number" && (
                      <span
                        className={`text-[11px] font-semibold px-1.5 py-0.2 rounded-full ${
                          isActive
                            ? "bg-primary-foreground/20 text-primary-foreground"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Active Tab Panel */}
          <div className="transition-opacity duration-150">
            {currentTab?.content}
          </div>
        </main>
      </div>
    </div>
  );
}
